import { supabase } from '../clients/supabaseClient';

// ── Types ──────────────────────────────────────────────────────────────────────

export type AnalyticsParams = {
    from: string;
    to: string;
    topLimit?: number;
    storeId?: string;
};

type OrderRow = {
    id: string;
    status: string;
    created_at: string;
    store_id: string | null;
    delivery_fee: number | null;
};

export type AnalyticsResponse = {
    kpis: {
        total_orders: number;
        revenue: number;
        cancelled_revenue?: number;
        wallet_refunds?: number;
        delivery_fee_revenue: number;
        product_revenue: number;
        avg_order_value: number;
        conversion_rate: number | null;
        deltas?: {
            total_orders?: number | null;
            revenue?: number | null;
            avg_order_value?: number | null;
            conversion_rate?: number | null;
            completed_orders?: number | null;
            cancelled_orders?: number | null;
            amount_refunded?: number | null;
        };
    };
    orders_by_status: { status: string; count: number }[];
    sales_over_time:  { date: string; orders: number; revenue: number }[];
    top_products: {
        product_id: string | number;
        sku: string;
        name: string;
        units_sold: number;
        revenue: number;
        price_buckets: { price: number; units_sold: number }[];
    }[];
    top_categories: {
        category_id: string;
        name: string;
        units_sold: number;
        revenue: number;
    }[];
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const dateKey = (iso: string) => {
    const d = new Date(iso);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};

async function fetchOrdersInRange(
    from: string,
    to: string,
    storeId?: string
): Promise<OrderRow[]> {
    let query = supabase
        .from('orders')
        .select('id, status, created_at, store_id, delivery_fee')
        .gte('created_at', from)
        .lte('created_at', to);

    if (storeId) query = query.eq('store_id', storeId);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as OrderRow[];
}

async function buildRevenueFromOrders(
    orderRows: OrderRow[],
    options: { includeProductBreakdowns?: boolean; topLimit?: number } = {}
) {
    const { includeProductBreakdowns = false, topLimit = 10 } = options;

    const completedRows = orderRows.filter(o => o.status === 'DELIVERY_COMPLETE');
    const completedIds  = completedRows.map(o => o.id);
    const allIds        = orderRows.map(o => o.id);

    const dayMap    = new Map<string, { orders: number; revenue: number }>();
    const statusMap = new Map<string, number>();

    for (const row of orderRows) {
        const status = row.status ?? 'UNKNOWN';
        statusMap.set(status, (statusMap.get(status) ?? 0) + 1);
        const key   = dateKey(row.created_at);
        const entry = dayMap.get(key) ?? { orders: 0, revenue: 0 };
        if (row.status === 'DELIVERY_COMPLETE') entry.orders += 1;
        dayMap.set(key, entry);
    }

    let totalProductCents  = 0;
    let totalDeliveryCents = 0;
    let totalRefundsCents  = 0;

    const productAgg = new Map<string, {
        product_id: string;
        sku: string;
        name: string;
        units_sold: number;
        revenue: number;
        price_buckets: Map<number, number>;
    }>();

    const categoryAgg = new Map<string, {
        category_id: string;
        name: string;
        units_sold: number;
        revenue: number;
    }>();

    if (allIds.length > 0) {
        const [itemsRes, walletRes] = await Promise.all([
            supabase
                .from('order_items')
                .select('order_id, product_id, quantity, product:products(id, sku, name), price_record:product_prices(price)')
                .in('order_id', completedIds)
                .is('deleted_at', null),
            supabase
                .from('wallet_transactions')
                .select('amount_cents, source_order_id, direction')
                .in('source_order_id', allIds)
                .eq('direction', 'credit'),
        ]);

        if (itemsRes.error)  throw itemsRes.error;
        if (walletRes.error) throw walletRes.error;

        for (const tx of walletRes.data ?? []) {
            totalRefundsCents += Number(tx.amount_cents ?? 0);
        }

        for (const row of itemsRes.data ?? []) {
            const product    = Array.isArray(row.product)    ? row.product[0]    : row.product;
            const priceRecord = Array.isArray(row.price_record) ? row.price_record[0] : row.price_record;
            const priceCents = Number(priceRecord?.price ?? 0);
            const qty        = Number(row.quantity ?? 0);
            const lineCents  = qty * priceCents;


            totalProductCents += lineCents;

            const matchingOrder = completedRows.find(o => o.id === row.order_id);
            if (matchingOrder) {
                const key   = dateKey(matchingOrder.created_at);
                const entry = dayMap.get(key) ?? { orders: 0, revenue: 0 };
                entry.revenue += lineCents;
                dayMap.set(key, entry);
            }

            const id = String(product?.id ?? row.product_id ?? 'unknown');
            const existing = productAgg.get(id) ?? {
                product_id:    id,
                sku:           String(product?.sku  ?? ''),
                name:          String(product?.name ?? 'Unnamed'),
                units_sold:    0,
                revenue:       0,
                price_buckets: new Map<number, number>(),
            };
            existing.units_sold += qty;
            existing.revenue    += lineCents;
            existing.price_buckets.set(priceCents, (existing.price_buckets.get(priceCents) ?? 0) + qty);
            productAgg.set(id, existing);
        }

        if (includeProductBreakdowns) {
            const topProductIds = Array.from(productAgg.keys()).slice(0, topLimit);
            if (topProductIds.length > 0) {
                const { data: catLinks, error: catErr } = await supabase
                    .from('category_products')
                    .select('product_id, category_id, categories(id, name)')
                    .in('product_id', topProductIds);

                if (catErr) throw catErr;

                for (const link of catLinks ?? []) {
                    const cat = Array.isArray(link.categories) ? link.categories[0] : link.categories;
                    if (!cat) continue;
                    const prod = productAgg.get(link.product_id);
                    if (!prod) continue;
                    const catId    = String(cat.id);
                    const existing = categoryAgg.get(catId) ?? {
                        category_id: catId, name: String(cat.name), units_sold: 0, revenue: 0,
                    };
                    existing.units_sold += prod.units_sold;
                    existing.revenue    += prod.revenue;
                    categoryAgg.set(catId, existing);
                }
            }
        }
    }

    for (const row of orderRows) {
        if (row.status === 'DELIVERY_COMPLETE') {
            const feeCents = Number(row.delivery_fee ?? 0);
            totalDeliveryCents += feeCents;
            const entry = dayMap.get(dateKey(row.created_at));
            if (entry) entry.revenue += feeCents;
        }
    }

    const completedCount = completedRows.length;
    const totalRevenue   = (totalProductCents + totalDeliveryCents) / 100;

    const calcDelta = (cur: number, prev: number): number | null => {
        if (prev === 0) return cur === 0 ? 0 : null;
        return ((cur - prev) / prev) * 100;
    };

    return {
        total_orders:         orderRows.length,
        completed_orders:     completedCount,
        revenue:              totalRevenue,
        wallet_refunds:       totalRefundsCents  / 100,
        product_revenue:      totalProductCents  / 100,
        delivery_fee_revenue: totalDeliveryCents / 100,
        avg_order_value:      completedCount > 0 ? totalRevenue / completedCount : 0,
        calcDelta,
        orders_by_status: Array.from(statusMap.entries())
            .map(([status, count]) => ({ status, count }))
            .sort((a, b) => b.count - a.count),
        sales_over_time: Array.from(dayMap.entries())
            .map(([date, v]) => ({ date, orders: v.orders, revenue: v.revenue / 100 }))
            .sort((a, b) => (a.date < b.date ? -1 : 1)),
        top_products: includeProductBreakdowns
            ? Array.from(productAgg.values())
                .map(p => ({
                    product_id:   p.product_id,
                    sku:          p.sku,
                    name:         p.name,
                    units_sold:   p.units_sold,
                    revenue:      p.revenue / 100,
                    price_buckets: Array.from(p.price_buckets.entries())
                        .map(([priceCents, units]) => ({ price: priceCents / 100, units_sold: units }))
                        .sort((a, b) => a.price - b.price),
                }))
                .sort((a, b) => b.units_sold - a.units_sold)
                .slice(0, topLimit)
            : [],
        top_categories: includeProductBreakdowns
            ? Array.from(categoryAgg.values())
                .map(c => ({ category_id: c.category_id, name: c.name, units_sold: c.units_sold, revenue: c.revenue / 100 }))
                .sort((a, b) => b.revenue - a.revenue)
                .slice(0, topLimit)
            : [],
    };
}

// ── Public API ─────────────────────────────────────────────────────────────────

export async function fetchAnalyticsFromDb(params: AnalyticsParams): Promise<AnalyticsResponse> {
    const { from, to, topLimit = 10, storeId } = params;

    const currentFrom  = new Date(from);
    const currentTo    = new Date(to);
    const periodMs     = currentTo.getTime() - currentFrom.getTime();
    const previousFrom = new Date(currentFrom.getTime() - periodMs - 1);
    const previousTo   = new Date(currentTo.getTime()   - periodMs - 1);

    const [currentOrders, previousOrders] = await Promise.all([
        fetchOrdersInRange(from, to, storeId),
        fetchOrdersInRange(previousFrom.toISOString(), previousTo.toISOString(), storeId),
    ]);

    const [current, previous] = await Promise.all([
        buildRevenueFromOrders(currentOrders,  { includeProductBreakdowns: true,  topLimit }),
        buildRevenueFromOrders(previousOrders, { includeProductBreakdowns: false, topLimit }),
    ]);

    return {
        kpis: {
            total_orders:         current.total_orders,
            revenue:              current.revenue,
            product_revenue:      current.product_revenue,
            wallet_refunds:       current.wallet_refunds,
            delivery_fee_revenue: current.delivery_fee_revenue,
            avg_order_value:      current.avg_order_value,
            conversion_rate:      null,
            deltas: {
                total_orders:     current.calcDelta(current.total_orders,     previous.total_orders),
                revenue:          current.calcDelta(current.revenue,          previous.revenue),
                avg_order_value:  current.calcDelta(current.avg_order_value,  previous.avg_order_value),
                completed_orders: current.calcDelta(current.completed_orders, previous.completed_orders),
                conversion_rate:  null,
            },
        },
        orders_by_status: current.orders_by_status,
        sales_over_time:  current.sales_over_time,
        top_products:     current.top_products,
        top_categories:   current.top_categories,
    };
}